import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import "./DashboardpagesCss/ReportGraph.css";
import nodatafound from "../../Assets/noDataFound.png";
import { FaPlus, FaBars } from "react-icons/fa";
import toast from "react-hot-toast";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getCompareData,
  getGraphDetails,
  getProfileStatus,
  getTestAllowed,
} from "../../api/api";
import { getUserMobile } from "../../utils/auth";

const ReportGraphPage = () => {
  const { members } = useOutletContext();
  const [selectedPatient, setSelectedPatient] = useState("");
  const [profileData, setProfileData] = useState([]);
  const [graphDetails, setGraphDetails] = useState([]);
  const [otherLabTests, setOtherLabTests] = useState([]);
  const [showOtherLab, setShowOtherLab] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("");

  // ✅ Fetch patient profile list
  useEffect(() => {
    fetchProfileListData();
  }, []);

  const fetchProfileListData = async () => {
    try {
      const mobile = getUserMobile();
      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
        user_id: "",
        mobile_no: mobile,
      };

      setLoading(true);
      const response = await getProfileStatus(payload);

      if (response?.data?.status === "success") {
        setProfileData(response.data.data || []);
      } else {
        toast.error(response?.data?.message || "Unable to fetch profile data");
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
      toast.error("Something went wrong while loading your profile.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch graph data for selected patient
  const FetchGraphDetails = async (userId) => {
    try {
      setLoading(true);
      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
        user_id: userId.slice(-7),
      };
      const response = await getGraphDetails(payload);

      if (response?.data?.status === "success") {
        const rawData = response.data.data || [];

        const grouped = rawData.reduce((acc, item) => {
          const testName = item.TestName;
          if (!acc[testName]) acc[testName] = [];
          acc[testName].push({
            date: new Date(item.TDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            value: item.Quantitative,
            unit: item.CompoUnit,
            refRange: item.CompoRefRange,
          });
          return acc;
        }, {});

        const formatted = Object.keys(grouped).map((key) => ({
          testName: key,
          graphData: grouped[key],
        }));

        setGraphDetails(formatted);
        setShowOtherLab(false);
      } else {
        toast.error("No graph data found");
        setGraphDetails([]);
      }
    } catch (error) {
      console.error("Error fetching graph data:", error);
      toast.error("Error loading graph data");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch “Test Done at Other Lab” list
  const fetchTestDoneOtherPlaceList = async () => {
    try {
      setLoading(true);
      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
      };

      const response = await getTestAllowed(payload);
      if (response?.data?.status === "success") {
        const data = response.data.data || [];
        setOtherLabTests(data);
        setShowOtherLab(true);
        toast.success("Other Lab Tests Loaded Successfully");
      } else {
        toast.error("No records found from other labs");
        setOtherLabTests([]);
      }
    } catch (error) {
      console.error("Error fetching tests from other labs:", error);
      toast.error("Error fetching tests from other labs");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handlers
  const handlePatientChange = (userId) => {
    setSelectedPatient(userId);
    setSelectedTest(null);

    const selected = profileData.find((p) => p.user_id === userId);
    if (selected) {
      setSelectedPatientName(
        `${selected.PFirstName} ${selected.PLastName}`.trim()
      );
    } else {
      setSelectedPatientName("");
    }

    if (userId) FetchGraphDetails(userId);
  };

  const handleTestDoneClick = () => {
    fetchTestDoneOtherPlaceList();
  };

  const handleViewGraph = (test) => {
    setSelectedTest(test);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTest(null);
  };

  // Dummy download handler
  const fetchCompareList = async (testId) => {
    try {
      setLoading(true);
      const payload = {
        RBAuthKey: "RBDWAh!Q1s74e",
        testid: testId,
      };

      const response = await getCompareData(payload);

      if (response?.data?.status === "success") {
        setCompareData(response.data.data || {});
        setShowAddModal(true);
      } else {
        toast.error("Unable to fetch compare data");
      }
    } catch (error) {
      console.error("Error fetching compare data:", error);
      toast.error("Error fetching compare data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="profile-header">
        <h2 className="rb-title pk-title package-tagg">Graph Report</h2>
      </div>

      <div className="edit-profile-form-wrapper">
          <span className="helper-text">
            Unable to find all your past values?
          </span>
        <div className="report-graph-controls">
          
          <div>
            {/* <span className="helper-text">
            Unable to find all your past values?
          </span> */}
            <select
              value={selectedPatient}
              onChange={(e) => handlePatientChange(e.target.value)}
              className="form-select select-form-view"
            >
              <option value="">-- Select Member --</option>
              {profileData.map((m, index) => (
                <option key={index} value={m.user_id}>
                  {m.PFirstName} {m.PLastName}
                </option>
              ))}
            </select>
          </div>

          {/* <span className="helper-text">
            Unable to find all your past values?
          </span> */}

          <div className="btn-group">
            <button
              className="view-dash-btn"
              onClick={() => {
                const mobile = getUserMobile();
                if (mobile) {
                  window.open(
                    `https://rbdiagnostic.com/mergereview/redirecting/${mobile}`,
                    "_blank"
                  );
                } else {
                  toast.error("Registered mobile number not found");
                }
              }}
            >
              <span className="btn-text">Test Done With Other Phone No.</span>
            </button>
            <button className="view-dash-btn" onClick={handleTestDoneClick}>
              <span className="btn-text">Test Done At Other Lab</span>
            </button>
          </div>
        </div>

        {/* Table Section */}
        {showOtherLab ? (
          otherLabTests.length > 0 ? (
            <table className="graph-table">
              <thead>
                <tr>
                  <th>Sl. No</th>
                  <th>Test Name</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {otherLabTests.map((test, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{test.testname}</td>
                    <td className="action-buttons">
                      <button
                        className="icon-btn view-icon"
                        title="View Graph"
                        onClick={() => fetchCompareList(test.testid)}
                      >
                        <FaPlus />
                      </button>
                      <button
                        className="icon-btn download-icon"
                        title="Download Report"
                      >
                        <FaBars />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            !loading && (
              <div className="no-data">
                <img src={nodatafound} alt="No Data" />
              </div>
            )
          )
        ) : graphDetails.length > 0 ? (
          <table className="graph-table">
            <thead>
              <tr>
                <th>Sl. No</th>
                <th>Test Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {graphDetails.map((test, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{test.testName}</td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => handleViewGraph(test)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          !loading && (
            <div className="no-data">
              <img src={nodatafound} alt="No Data" />
            </div>
          )
        )}
      </div>

      {/* 🔹 Modal for Graph */}
      {showModal && selectedTest && (
        <div className="graph-modal-overlay" onClick={closeModal}>
          <div className="graph-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeModal}>
              ✕
            </button>
            <h4>{selectedTest.testName || selectedTest.TestName} Trend</h4>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={selectedTest.graphData || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#0fb7a4"
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Modal For compareList */}
      {showAddModal && compareData && (
        <div
          className="graph-modal-overlay"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="add-values-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-btn"
              onClick={() => setShowAddModal(false)}
            >
              ✕
            </button>

            <h3 className="add-modal-title">Add New Values</h3>

            <div className="patient-name">{selectedPatientName}</div>

            <div className="note-box">
              <strong>Note :</strong> The Non-RB Tests graph will be visible
              once you've added data for at least three consecutive tests.
            </div>

            <div className="form-section">
              <p>
                <strong>Test Name :</strong>{" "}
                {compareData.TestName || "Creatinine"}
              </p>

              <p>
                <strong>Select Date :</strong>{" "}
                {new Date().toLocaleDateString("en-GB")}
                <span className="calendar-icon">📅</span>
              </p>

              <p className="sub-note">
                <strong>Note :</strong> For an accurate graph, input the values
                exactly as they appear in Non RB diagnostic Report.
              </p>

              <div className="component-box">
                <label>
                  Component :{" "}
                  <strong>{compareData.Component || "Creatinine"}</strong>
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    placeholder="Enter Numeric Value"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <span className="unit">
                    {compareData.CompoUnit || "mg/dL"}
                  </span>
                </div>
              </div>
            </div>

            <button
              className="save-btn"
              onClick={() => toast.success("Saved successfully!")}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportGraphPage;
